import { createApp, h, type App } from 'vue'
import loadingGif from '@/assets/images/loading.gif'

/**
 * PageLoading 服务选项
 */
type PageLoadingOptions = {
  /**
   * 是否全屏显示
   * @default true
   */
  fullscreen?: boolean
  /**
   * 加载文本
   * @default 'Loading...'
   */
  text?: string
  /**
   * 背景颜色
   * @default 'rgba(0, 0, 0, 0.7)'
   */
  background?: string
  /**
   * 是否锁定滚动
   * @default true
   */
  lock?: boolean
}

/**
 * PageLoading 服务实例
 */
export type PageLoadingInstance = {
  /**
   * 关闭 loading
   */
  close: () => void
}

/**
 * 创建 PageLoading 组件
 */
const createPageLoadingComponent = (options: Required<PageLoadingOptions>) => {
  return {
    setup() {
      return () =>
        h('div', {
          class: 'fixed inset-0 flex flex-col items-center justify-center gap-2 z-[9999] w-full h-full',
          style: {
            background: options.background,
          },
        }, [
          h('img', {
            src: loadingGif,
            alt: '',
            class: 'w-16 h-16',
          }),
          h('span', {
            class: 'text-white text-base font-medium',
          }, options.text),
        ])
    },
  }
}

/**
 * 当前活动的 loading 实例
 */
let currentInstance: { app: App; container: HTMLElement; lock: boolean } | null = null

/**
 * PageLoading 服务
 * 用法: const loading = PageLoading.service({ fullscreen: true })
 *       loading.close()
 */
export const PageLoading = {
  service(options: PageLoadingOptions = {}): PageLoadingInstance {
    const {
      fullscreen = true,
      text = 'Loading...',
      background = 'rgba(0, 0, 0, 0.7)',
      lock = true,
    } = options

    // 如果已有实例，先关闭
    if (currentInstance) {
      this.close()
    }

    // 创建容器
    const container = document.createElement('div')
    container.setAttribute('data-page-loading-service', 'true')

    // 添加到 body
    document.body.appendChild(container)

    // 如果锁定滚动，添加样式
    if (lock && fullscreen) {
      document.body.style.overflow = 'hidden'
    }

    // 创建 Vue 应用实例
    const component = createPageLoadingComponent({
      fullscreen,
      text,
      background,
      lock,
    })
    const app = createApp(component)
    app.mount(container)

    // 保存实例
    currentInstance = { app, container, lock: lock && fullscreen }

    // 返回关闭方法
    return {
      close: () => {
        if (currentInstance) {
          currentInstance.app.unmount()
          if (currentInstance.container.parentNode) {
            currentInstance.container.parentNode.removeChild(currentInstance.container)
          }
          // 恢复滚动
          if (currentInstance.lock) {
            document.body.style.overflow = ''
          }
          currentInstance = null
        }
      },
    }
  },

  /**
   * 关闭当前活动的 loading
   */
  close(): void {
    if (currentInstance) {
      currentInstance.app.unmount()
      if (currentInstance.container.parentNode) {
        currentInstance.container.parentNode.removeChild(currentInstance.container)
      }
      // 恢复滚动
      if (currentInstance.lock) {
        document.body.style.overflow = ''
      }
      currentInstance = null
    }
  },
}
